import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Search, Download, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, ChevronDown, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ClickableSKU } from "@/components/drilldown/Clickables";
import { useDrillDown } from "@/components/drilldown/DrillDownContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import inventory from "@/api/inventory";
import inventoryPdf, { pdfErrorMessage } from "@/api/inventoryPdf";
import PdfExportButton from "./PdfExportButton";

const COLUMNS = [
  { key: "sku",                label: "SKU",                 sortable: true,  numeric: false },
  { key: "description",        label: "Descripción",         sortable: false, numeric: false },
  { key: "category",           label: "Categoría",           sortable: false, numeric: false },
  { key: "entradas_qty",       label: "Entradas",            sortable: true,  numeric: true  },
  { key: "salidas_qty",        label: "Salidas",             sortable: true,  numeric: true  },
  { key: "delta_neto",         label: "Δ Neto",              sortable: true,  numeric: true  },
  { key: "stock_calc",         label: "Stock actual",        sortable: false, numeric: true  },
  { key: "ultimo_movimiento",  label: "Último movimiento",   sortable: true,  numeric: false },
  { key: "costo_total",        label: "Costo total",         sortable: true,  numeric: true  },
];

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US");
}

function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function fmtDate(s) {
  if (!s) return "—";
  return String(s).slice(0, 10);
}

export default function InventorySummaryTable({
  filters,
  testId = "inventory-summary-table",
  apiOverride,
  hideExport = false,
}) {
  const { t } = useTranslation();
  const { openProductModal } = useDrillDown();
  const api = apiOverride || inventory;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("salidas_qty");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Reset page when filters change.
  useEffect(() => {
    setPage(1);
  }, [filters?.preset, filters?.date_from, filters?.date_to, filters?.source_report, search]);

  // Debounced search.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getSummaryTable(filters, { page, pageSize, sortBy, sortDir, search })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(
            t("inventory.errors.summaryLoad", {
              defaultValue: "No se pudo cargar la tabla de resumen",
            })
          );
          console.error(e);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filters, page, pageSize, sortBy, sortDir, search, t]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((data.total || 0) / pageSize)),
    [data.total, pageSize]
  );

  const handleSort = (key) => {
    const col = COLUMNS.find((c) => c.key === key);
    if (!col?.sortable) return;
    if (sortBy === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(key);
      setSortDir(col.numeric ? "desc" : "asc");
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await api.downloadExport(filters, { sortBy, sortDir, search });
      toast.success(
        t("inventory.export.success", { defaultValue: "Excel descargado" })
      );
    } catch (e) {
      toast.error(
        t("inventory.export.error", { defaultValue: "Fallo al exportar Excel" })
      );
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      await inventoryPdf.summaryTable(filters, { sortBy, sortDir, search });
      toast.success(
        t("inventory.exportPdf.success", { defaultValue: "PDF descargado" })
      );
    } catch (e) {
      toast.error(pdfErrorMessage(e, t));
      console.error(e);
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            data-testid="summary-search-input"
            placeholder={t("inventory.searchPlaceholder", { defaultValue: "Buscar SKU o descripción..." })}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-7 h-8 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
            <SelectTrigger className="h-8 w-[90px]" data-testid="page-size-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            data-testid="summary-export-btn"
            onClick={handleExport}
            disabled={exporting || loading}
            hidden={hideExport}
            className={hideExport ? "hidden" : ""}
          >
            {exporting
              ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              : <Download className="h-3.5 w-3.5 mr-1.5" />}
            {t("inventory.exportExcel", { defaultValue: "Exportar Excel" })}
          </Button>
          <PdfExportButton
            onClick={handleExportPdf}
            exporting={exportingPdf}
            disabled={loading}
            hidden={hideExport}
            testId="summary-pdf-btn"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMNS.map((col) => {
                  const isActive = sortBy === col.key;
                  const Icon = !col.sortable
                    ? null
                    : isActive
                      ? (sortDir === "desc" ? ChevronDown : ChevronUp)
                      : ChevronsUpDown;
                  return (
                    <TableHead
                      key={col.key}
                      data-testid={`th-${col.key}`}
                      className={`text-xs ${col.numeric ? "text-right" : ""} ${col.sortable ? "cursor-pointer select-none" : ""}`}
                      onClick={() => handleSort(col.key)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {Icon && (
                          <Icon
                            className={`h-3 w-3 ${isActive ? "text-primary" : "text-muted-foreground/60"}`}
                          />
                        )}
                      </span>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && data.items.length === 0 ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {COLUMNS.map((c) => (
                      <TableCell key={c.key}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} className="text-center text-muted-foreground py-8">
                    {t("inventory.empty", { defaultValue: "Sin resultados para los filtros seleccionados" })}
                  </TableCell>
                </TableRow>
              ) : (
                data.items.map((row) => (
                  <TableRow key={row.sku} data-testid={`row-${row.sku}`}>
                    <TableCell className="font-mono text-xs">
                      <ClickableSKU sku={row.sku} onOpen={openProductModal}>
                        {row.sku}
                      </ClickableSKU>
                    </TableCell>
                    <TableCell className="text-xs max-w-[280px] truncate">{row.description || "—"}</TableCell>
                    <TableCell className="text-xs">{row.category || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-emerald-700 dark:text-emerald-300">
                      {fmtNum(row.entradas_qty)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-rose-700 dark:text-rose-300">
                      {fmtNum(row.salidas_qty)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums text-xs ${row.delta_neto < 0 ? "text-rose-700" : "text-foreground"}`}>
                      {row.delta_neto > 0 ? "+" : ""}{fmtNum(row.delta_neto)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtNum(row.stock_calc)}</TableCell>
                    <TableCell className="text-xs tabular-nums">{fmtDate(row.ultimo_movimiento)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtMoney(row.costo_total)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <div>
          {t("inventory.paginationLabel", {
            from: ((page - 1) * pageSize + 1).toLocaleString("en-US"),
            to: Math.min(page * pageSize, data.total).toLocaleString("en-US"),
            total: (data.total || 0).toLocaleString("en-US"),
            defaultValue: `${((page - 1) * pageSize + 1).toLocaleString("en-US")}–${Math.min(page * pageSize, data.total).toLocaleString("en-US")} de ${(data.total || 0).toLocaleString("en-US")}`,
          })}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="page-prev-btn"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="tabular-nums px-2" data-testid="current-page">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="page-next-btn"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
